import { FormattedMessage, useIntl } from 'react-intl';
import { ResponsiveLine } from '@nivo/line';
import { Button, Form, Modal, Typography } from 'antd';

import { currencyFormatter, metricPrefixFormatter } from '@/utils/number';

import { ContentWrapper, EllipsisButton } from '@/components/_common';

import { ContentWrapperProps } from '@/components/_common/ContentWrapper';
import { useChartPartsAndServices } from '@/core/domain/workorder/ManagementCharts/ManagementChart.hooks';
import {
  useChartTheme,
  useConvertChartData,
  useDisclosure,
  useWindowSize,
} from '@/hooks';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { DateTimeFormat } from '@/utils/moment';
import {
  PartsServicesFormValues,
  PartsServicesParams,
  PartsServicesStatus,
} from '@/core/domain/workorder/ManagementCharts/ManagementChart.types';
import PartsAndServicesModalForm from './PartsAndServicesModalForm';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import RedirectIcon from '@/components/_common/RedirectIcon';
import { buildParamsStr } from '@/utils/queryParams';
import {
  QuestFilterDateBy,
  QuestReportType,
  QuestSituationOptions,
} from '@/core/domain/questReport/Reports/WorkOrder/WorkOrder.types';
import { Group } from 'antd/lib/avatar';

const { Title, Paragraph } = Typography;

function PartsAndServicesChart({ ...props }: ContentWrapperProps) {
  const { isMobile } = useWindowSize();
  const [form] = Form.useForm<PartsServicesFormValues>();
  const intl = useIntl();
  const [modalVisible, modalVisibleAction] = useDisclosure(false);
  const chartTheme = useChartTheme();

  const [filter, setFilter] = useState<PartsServicesParams>({
    startDate: moment().subtract(5, 'months').format(DateTimeFormat),
    endDate: moment().format(DateTimeFormat),
    status: [PartsServicesStatus.closed],
  });

  const { data, mutateAsync, isLoading } = useChartPartsAndServices();

  const chartData = useConvertChartData(data?.chartData);

  useEffect(() => {
    mutateAsync(filter);
  }, []);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      const obj: PartsServicesParams = {
        companiesIds: values.companiesIds,
        startDate: values.dates[0].format('YYYY-MM-DD'),
        endDate: values.dates[1].format('YYYY-MM-DD'),
        status: values.status,
      };

      setFilter(obj);

      await mutateAsync(obj);

      modalVisibleAction.close();
    } catch {}
  };

  const handleModalReset = async () => {
    const startDate = moment().subtract(5, 'months');
    const endDate = moment();

    const obj = {
      startDate: startDate.format(DateTimeFormat),
      endDate: endDate.format(DateTimeFormat),
      status: [PartsServicesStatus.closed],
    };

    setFilter(obj);

    await mutateAsync(obj);

    modalVisibleAction.close();
    form.resetFields();
  };

  const paramsRedirectReport = {
    initialDate: filter.startDate,
    finalDate: filter.endDate,
    companiesIds: filter.companiesIds,
    reportType: QuestReportType.servicesAndPartsPerVehicle,
    filterDateBy: QuestFilterDateBy.closing,
    situation: filter.status?.map((item) =>
      item === PartsServicesStatus.closed
        ? QuestSituationOptions.closed
        : QuestSituationOptions.authorized,
    ),
    includeParts: true,
    includePartsProvided: true,
    includeServices: true,
    includeServicesProvided: true,
  };

  return (
    <>
      <ContentWrapper
        style={{
          height: '360px',
          padding: '24px',
        }}
        extra={
          <Group style={{ flexDirection: isMobile ? 'column' : undefined }}>
            <EllipsisButton onClick={modalVisibleAction.open} />

            <RedirectIcon
              placement="bottom"
              paths={[
                {
                  title: 'Relatório de Peças e Serviços',
                  url: `/app/reports/movements/workOrders?${buildParamsStr(
                    paramsRedirectReport,
                  )}`,
                },
              ]}
            />
          </Group>
        }
        {...props}
      >
        <Title level={5} style={{ textAlign: 'center', marginBottom: '4px' }}>
          <FormattedMessage id="dashboard.partsAndServicesReport" />
        </Title>

        <Paragraph style={{ textAlign: 'center', margin: '4px 0 4px 0' }}>
          <FormattedMessage id="dashboard.lastSixMonths" />
        </Paragraph>

        <ResponsiveLine
          data={chartData ? chartData : []}
          enableSlices="x"
          margin={{ top: 15, right: 25, bottom: 90, left: 70 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 'auto',
            reverse: false,
          }}
          yFormat={(value) => `${currencyFormatter(value)}`}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legendOffset: 36,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            tickValues: 5,
            legend: intl.formatMessage({
              id: 'dashboard.partsAndServicesReport.partsAndServicesTotal',
            }),
            format: (value) => `${metricPrefixFormatter(+value)}`,
            legendOffset: -50,
            legendPosition: 'middle',
          }}
          enableGridX={false}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh
          theme={chartTheme}
        />
      </ContentWrapper>

      <Modal
        title="Filtros"
        open={modalVisible}
        onCancel={modalVisibleAction.close}
        width={500}
        onOk={handleOk}
        footer={
          <SpaceForButton justifyContent="end">
            <Button
              type="primary"
              ghost
              loading={isLoading}
              onClick={handleModalReset}
            >
              Resetar
            </Button>

            <Button type="primary" onClick={handleOk} loading={isLoading}>
              Atualizar gráfico
            </Button>
          </SpaceForButton>
        }
      >
        <PartsAndServicesModalForm form={form} />
      </Modal>
    </>
  );
}

export default PartsAndServicesChart;
